import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, ArrowRight, Coins, ShieldCheck, KeyRound, AlertTriangle, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated dropdown groups — mirrors backend fx.SUPPORTED_CURRENCIES.
const CURRENCY_GROUPS = [
    { label: "Middle East", codes: ["QAR", "SAR", "AED", "KWD", "BHD", "OMR", "IQD"] },
    { label: "Central Asia", codes: ["UZS", "KZT", "KGS", "TJS", "TMT"] },
    { label: "Global reserve", codes: ["USD", "EUR", "GBP"] },
    { label: "South Asia", codes: ["INR", "PKR"] },
];

/**
 * CurrencyAuditPanel — surfaces every product/invoice/PO whose currency ≠ base,
 * lets the admin either relabel (currency tag only) or convert (multiply amounts
 * with an FX rate + full audit trail).
 *
 * Also hosts the Open Exchange Rates key field + refresh button so auto-rates
 * populate automatically once configured.
 */
export function CurrencyAuditPanel({ baseCurrency, s, change }) {
    const [audit, setAudit] = useState(null);
    const [fxStatus, setFxStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    // Modal state — track { collection, from_currency, records:[…] } while the
    // admin is deciding to relabel or convert.
    const [modal, setModal] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [a, fx] = await Promise.all([
                axios.get(`${API}/admin/currency-audit`),
                axios.get(`${API}/admin/fx/status`),
            ]);
            setAudit(a.data);
            setFxStatus(fx.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load currency audit");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const refreshFx = async () => {
        setRefreshing(true);
        try {
            const { data } = await axios.post(`${API}/admin/fx/refresh`);
            toast.success(`FX rates refreshed · ${data.symbols} symbols`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "OXR refresh failed");
        } finally {
            setRefreshing(false);
        }
    };

    const [backfilling, setBackfilling] = useState(false);
    const backfillBaseAmounts = async () => {
        if (!window.confirm("Stamp base_amount on all invoices and POs that don't have one? This is safe to re-run — records already stamped are skipped.")) return;
        setBackfilling(true);
        try {
            const { data } = await axios.post(`${API}/admin/currency-audit/backfill-base-amounts`);
            const inv = data.collections?.invoices || {};
            const po = data.collections?.purchase_orders || {};
            toast.success(
                `Backfilled invoices: ${inv.same_currency_stamped || 0} same-currency + ${inv.cross_currency_converted || 0} converted · POs: ${po.same_currency_stamped || 0} same + ${po.cross_currency_converted || 0} converted`,
                { duration: 8000 }
            );
            if ((inv.cross_currency_unavailable || 0) + (po.cross_currency_unavailable || 0) > 0) {
                toast.warning("Some cross-currency records had no OXR rate available — configure the OXR key above then re-run.");
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Backfill failed");
        } finally {
            setBackfilling(false);
        }
    };

    return (
        <section
            data-testid="currency-audit-panel"
            className="rounded-2xl border border-slate-200 bg-white p-6"
        >
            <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-cyan-100 flex items-center justify-center">
                    <Coins size={18} className="text-cyan-700" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-display text-[18px] tracking-tight text-slate-900">Currency audit</h3>
                    <p className="text-[12.5px] text-slate-500 mt-0.5">
                        Records not in your base currency (<strong className="font-mono-tr">{baseCurrency}</strong>). Choose &ldquo;Relabel&rdquo; if the currency tag is wrong; &ldquo;Convert with rate&rdquo; multiplies amounts and preserves history.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="currency-audit-reload"
                    className="tr-btn-ghost !py-2 !px-3 text-[12px]"
                >
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Reload
                </button>
            </div>

            {/* OXR configuration block */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 mb-5">
                <div className="flex items-center gap-2 mb-3">
                    <KeyRound size={14} className="text-slate-600" />
                    <span className="text-[12.5px] font-medium text-slate-900">Auto-rate provider · Open Exchange Rates</span>
                    {fxStatus?.configured ? (
                        <span className="ml-auto text-[10px] font-mono-tr tracking-wider uppercase text-emerald-700 flex items-center gap-1">
                            <Check size={11} /> connected
                        </span>
                    ) : (
                        <span className="ml-auto text-[10px] font-mono-tr tracking-wider uppercase text-slate-500">optional</span>
                    )}
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                    <label className="block sm:col-span-2">
                        <div className="text-[10.5px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 mb-1">OXR App ID</div>
                        <input
                            data-testid="setting-oxr_app_id"
                            type="password"
                            value={s.oxr_app_id || ""}
                            onChange={(e) => change("oxr_app_id", e.target.value)}
                            placeholder="Paste your OXR App ID"
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-[13px] font-mono-tr"
                        />
                        <div className="mt-1 text-[11px] text-slate-500">
                            Free tier: 1,000 requests/month. Sign up at{" "}
                            <a href="https://openexchangerates.org/signup/free" target="_blank" rel="noreferrer" className="text-cyan-600 underline">openexchangerates.org</a>.
                            Save Settings first, then click Refresh below.
                        </div>
                    </label>
                    <label className="block">
                        <div className="text-[10.5px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 mb-1">Cache TTL (hours)</div>
                        <input
                            data-testid="setting-fx_cache_ttl_hours"
                            type="number"
                            min="1"
                            max="72"
                            value={s.fx_cache_ttl_hours || "12"}
                            onChange={(e) => change("fx_cache_ttl_hours", e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 outline-none text-[13px] font-mono-tr"
                        />
                    </label>
                </div>
                <div className="mt-3 flex items-center gap-3 text-[11.5px] text-slate-600">
                    <button
                        onClick={refreshFx}
                        disabled={refreshing || !fxStatus?.configured}
                        data-testid="fx-refresh-btn"
                        className="tr-btn-ghost !py-1.5 !px-3 text-[12px] disabled:opacity-50"
                    >
                        {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                        Refresh rates now
                    </button>
                    {fxStatus?.cache?.fetched_at && (
                        <span className="text-slate-500">
                            Cached {new Date(fxStatus.cache.fetched_at).toLocaleString()} · expires {new Date(fxStatus.cache.expires_at).toLocaleString()}
                        </span>
                    )}
                </div>
            </div>

            {/* Base-amount backfill (iter 84 P2). One-click stamp so mixed-currency
                dashboards + Finance monthly P&L can sum across records without
                runtime FX. Safe to re-run — records already stamped are skipped. */}
            <div className="rounded-xl border border-cyan-200 bg-cyan-50/40 p-4 mb-5" data-testid="base-amount-backfill">
                <div className="flex items-start gap-3">
                    <ShieldCheck size={16} className="text-cyan-700 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium text-slate-900">Base-amount backfill</div>
                        <div className="text-[11.5px] text-slate-600 mt-0.5 leading-relaxed">
                            Stamps <code className="font-mono-tr text-cyan-800">base_amount</code> + <code className="font-mono-tr text-cyan-800">fx_rate_used</code> on every existing invoice and PO so dashboards + Finance monthly P&amp;L can roll up mixed-currency records. Same-currency rows use rate 1.0; cross-currency rows use today&apos;s OXR rate. Safe to re-run — already-stamped records are skipped.
                        </div>
                    </div>
                    <button
                        onClick={backfillBaseAmounts}
                        disabled={backfilling}
                        data-testid="fx-backfill-btn"
                        className="tr-btn-ghost !py-1.5 !px-3 text-[12px] disabled:opacity-50"
                    >
                        {backfilling ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                        Backfill now
                    </button>
                </div>
            </div>

            {/* Audit results */}
            {loading && !audit ? (
                <div className="flex items-center gap-2 text-slate-500 text-[13px] py-4">
                    <Loader2 size={14} className="animate-spin" /> Scanning records&hellip;
                </div>
            ) : audit ? (
                <AuditTable
                    audit={audit}
                    baseCurrency={baseCurrency}
                    onAction={(coll, cur) => setModal({ collection: coll, from_currency: cur })}
                />
            ) : null}

            {modal && (
                <CurrencyActionModal
                    modal={modal}
                    audit={audit}
                    baseCurrency={baseCurrency}
                    fxConfigured={!!fxStatus?.configured}
                    onClose={() => setModal(null)}
                    onSuccess={() => { setModal(null); load(); }}
                />
            )}
        </section>
    );
}

function AuditTable({ audit, baseCurrency, onAction }) {
    const collections = audit.collections || {};
    const entries = Object.entries(collections);
    const totalOff = entries.reduce((n, [, v]) => n + (v.total_off_base || 0), 0);

    if (totalOff === 0) {
        return (
            <div
                data-testid="currency-audit-clean"
                className="rounded-xl border border-emerald-200 bg-emerald-50/40 px-4 py-5 flex items-center gap-3"
            >
                <ShieldCheck size={20} className="text-emerald-600" />
                <div>
                    <div className="text-[14px] font-medium text-emerald-900">All clear.</div>
                    <div className="text-[12.5px] text-emerald-700/80">
                        Every product, invoice, and purchase order is in <strong className="font-mono-tr">{baseCurrency}</strong>.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {entries.map(([collName, block]) => {
                if (!block.total_off_base) return null;
                return (
                    <div key={collName} data-testid={`audit-block-${collName}`} className="rounded-xl border border-amber-200 bg-amber-50/30 overflow-hidden">
                        <div className="px-4 py-2.5 bg-amber-50/60 border-b border-amber-200/70 flex items-center gap-2">
                            <AlertTriangle size={14} className="text-amber-700" />
                            <span className="text-[13px] font-medium text-slate-900 capitalize">{collName.replace(/_/g, " ")}</span>
                            <span className="text-[11px] font-mono-tr text-amber-700/80">· {block.total_off_base} record(s) not in {baseCurrency}</span>
                        </div>
                        <div className="p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {Object.entries(block.by_currency).map(([cur, info]) => (
                                <div key={cur} className="rounded-lg border border-slate-200 bg-white p-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="font-mono-tr text-[13px] text-slate-900">{cur}</div>
                                        <div className="text-[11px] text-slate-500">{info.count} record(s)</div>
                                    </div>
                                    <ul className="text-[11.5px] text-slate-600 space-y-1 mb-3">
                                        {(info.sample || []).slice(0, 3).map((row) => (
                                            <li key={row.id} className="truncate">
                                                <span className="text-slate-400 mr-1">·</span>{row.display}
                                            </li>
                                        ))}
                                        {info.count > 3 && <li className="text-slate-400">…and {info.count - 3} more</li>}
                                    </ul>
                                    <button
                                        onClick={() => onAction(collName, cur)}
                                        data-testid={`audit-action-${collName}-${cur}`}
                                        className="tr-btn-ghost !py-1.5 !px-3 text-[11.5px] w-full justify-center"
                                    >
                                        Review + fix <ArrowRight size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function CurrencyActionModal({ modal, audit, baseCurrency, fxConfigured, onClose, onSuccess }) {
    const { collection, from_currency } = modal;
    const info = audit?.collections?.[collection]?.by_currency?.[from_currency];
    const sample = info?.sample || [];

    const [toCurrency, setToCurrency] = useState(baseCurrency);
    const [mode, setMode] = useState("relabel"); // "relabel" | "convert"
    const [rate, setRate] = useState("");
    const [rateSource, setRateSource] = useState("manual");
    const [selectedIds, setSelectedIds] = useState(sample.map((r) => r.id));
    const [busy, setBusy] = useState(false);
    const [fetchingRate, setFetchingRate] = useState(false);

    const toggleId = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const fetchRate = async () => {
        setFetchingRate(true);
        try {
            const { data } = await axios.get(`${API}/admin/fx/rate`, {
                params: { from_currency, to_currency: toCurrency },
            });
            setRate(String(Number(data.rate).toFixed(6)));
            setRateSource("oxr");
            toast.success(`Rate fetched: 1 ${from_currency} = ${Number(data.rate).toFixed(6)} ${toCurrency}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to fetch rate");
        } finally {
            setFetchingRate(false);
        }
    };

    const submit = async () => {
        if (selectedIds.length === 0) return toast.error("Select at least one record");
        setBusy(true);
        try {
            if (mode === "relabel") {
                const { data } = await axios.post(`${API}/admin/currency-audit/relabel`, {
                    collection, ids: selectedIds, from_currency, to_currency: toCurrency,
                });
                toast.success(`${data.modified} record(s) relabelled → ${toCurrency}`);
            } else {
                const r = parseFloat(rate);
                if (!r || r <= 0) { setBusy(false); return toast.error("Enter a valid FX rate"); }
                const { data } = await axios.post(`${API}/admin/currency-audit/convert`, {
                    collection, ids: selectedIds, from_currency, to_currency: toCurrency,
                    rate: r, rate_source: rateSource,
                });
                toast.success(`${data.modified} record(s) converted at ${r} → ${toCurrency}`);
            }
            onSuccess();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Operation failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="currency-action-modal"
            className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
                    <Coins size={16} className="text-cyan-600" />
                    <h4 className="font-display text-[16px] tracking-tight text-slate-900">
                        {collection} · {from_currency} <ArrowRight size={12} className="inline text-slate-400 mx-1" /> {toCurrency}
                    </h4>
                    <button onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700 text-[13px]" data-testid="modal-close">Close</button>
                </div>

                <div className="p-6 overflow-y-auto flex-1 space-y-5">
                    <div>
                        <div className="text-[10.5px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 mb-1.5">Target currency</div>
                        <select
                            value={toCurrency}
                            onChange={(e) => setToCurrency(e.target.value)}
                            data-testid="modal-to-currency"
                            className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none text-[14px] bg-white font-mono-tr"
                        >
                            {CURRENCY_GROUPS.map((g) => (
                                <optgroup key={g.label} label={g.label}>
                                    {g.codes.map((c) => <option key={c} value={c}>{c}</option>)}
                                </optgroup>
                            ))}
                        </select>
                    </div>

                    {/* Mode toggle */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setMode("relabel")}
                            data-testid="modal-mode-relabel"
                            className={`flex-1 rounded-lg border px-3 py-2.5 text-left transition ${mode === "relabel" ? "border-cyan-500 bg-cyan-50/60" : "border-slate-200 hover:border-slate-300"}`}
                        >
                            <div className="text-[13px] font-medium text-slate-900">Relabel only</div>
                            <div className="text-[11.5px] text-slate-500">Change the currency tag. Numeric amounts unchanged. Safe if the data was tagged wrong.</div>
                        </button>
                        <button
                            onClick={() => setMode("convert")}
                            data-testid="modal-mode-convert"
                            className={`flex-1 rounded-lg border px-3 py-2.5 text-left transition ${mode === "convert" ? "border-cyan-500 bg-cyan-50/60" : "border-slate-200 hover:border-slate-300"}`}
                        >
                            <div className="text-[13px] font-medium text-slate-900">Convert with FX rate</div>
                            <div className="text-[11.5px] text-slate-500">Multiply amounts by the rate. Full audit trail; reversible from the audit log.</div>
                        </button>
                    </div>

                    {mode === "convert" && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-2">
                            <div className="text-[10.5px] font-mono-tr tracking-[0.18em] uppercase text-slate-500">FX rate (1 {from_currency} = ? {toCurrency})</div>
                            <div className="flex gap-2">
                                <input
                                    type="number"
                                    step="0.000001"
                                    min="0"
                                    value={rate}
                                    onChange={(e) => { setRate(e.target.value); setRateSource("manual"); }}
                                    placeholder="0.045000"
                                    data-testid="modal-rate-input"
                                    className="flex-1 px-3 py-2 rounded-lg border border-slate-200 outline-none text-[14px] font-mono-tr"
                                />
                                <button
                                    onClick={fetchRate}
                                    disabled={fetchingRate || !fxConfigured || from_currency === toCurrency}
                                    data-testid="modal-fetch-rate"
                                    className="tr-btn-ghost !py-2 !px-3 text-[12px] disabled:opacity-50"
                                    title={!fxConfigured ? "Configure OXR App ID above to enable auto-fetch" : ""}
                                >
                                    {fetchingRate ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                                    Auto-fetch (OXR)
                                </button>
                            </div>
                            {rateSource === "oxr" && (
                                <div className="text-[11px] text-emerald-700 flex items-center gap-1">
                                    <Check size={11} /> Rate auto-populated from Open Exchange Rates.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Records */}
                    <div>
                        <div className="text-[10.5px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 mb-2 flex items-center gap-2">
                            Records ({sample.length} shown · {info?.count} total)
                            <button
                                onClick={() => setSelectedIds(selectedIds.length === sample.length ? [] : sample.map((r) => r.id))}
                                className="ml-auto text-cyan-600 underline"
                                data-testid="modal-toggle-all"
                            >
                                {selectedIds.length === sample.length ? "Clear" : "Select all"}
                            </button>
                        </div>
                        <div className="space-y-1.5 max-h-64 overflow-y-auto">
                            {sample.map((row) => (
                                <label key={row.id} className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(row.id)}
                                        onChange={() => toggleId(row.id)}
                                        data-testid={`modal-row-${row.id}`}
                                        className="accent-cyan-600"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] text-slate-900 truncate">{row.display}</div>
                                        <div className="text-[11px] text-slate-500 font-mono-tr">
                                            {Object.entries(row.amounts || {}).map(([k, v]) => `${k}: ${v} ${row.currency}`).join(" · ") || "no amount fields"}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                        {info?.count > sample.length && (
                            <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                                Only the first {sample.length} records are shown per batch. Run this action again after the update to catch the rest.
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-slate-200 flex items-center gap-3">
                    <div className="text-[11.5px] text-slate-500">{selectedIds.length} selected</div>
                    <button onClick={onClose} className="ml-auto tr-btn-ghost !py-2 !px-4 text-[12.5px]" data-testid="modal-cancel">Cancel</button>
                    <button
                        onClick={submit}
                        disabled={busy || selectedIds.length === 0 || (mode === "convert" && !parseFloat(rate))}
                        data-testid="modal-submit"
                        className="tr-btn !py-2 !px-4 text-[12.5px] disabled:opacity-50"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                        {mode === "relabel" ? "Relabel" : "Convert"} {selectedIds.length} record(s)
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CurrencyAuditPanel;
